import { useEffect, useState } from "react";
import { Users, Video, DollarSign, TrendingUp, Calendar, Sparkles } from "lucide-react";
import api from "@/lib/api";

function KPI({ label, value, sub, icon: Icon, testid }) {
  return (
    <div data-testid={testid} className="rounded-2xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] p-4">
      <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-[hsl(var(--muted-foreground))]">
        {Icon ? <Icon size={13} /> : null} {label}
      </div>
      <div className="mt-1.5 font-display text-2xl font-semibold tabular-nums">{value}</div>
      {sub ? <div className="mt-0.5 text-xs text-[hsl(var(--muted-foreground))]">{sub}</div> : null}
    </div>
  );
}

export default function AdminDashboard() {
  const [k, setK] = useState(null);

  useEffect(() => { api.get("/admin/kpis").then((r) => setK(r.data)).catch(() => {}); }, []);

  if (!k) return <div className="p-8"><div className="h-8 w-40 rounded bg-[hsl(var(--muted))] animate-pulse" /></div>;

  const rev = (k.revenue_cents_lifetime / 100).toFixed(2);
  const paidPct = k.users_total ? Math.round((k.paid_users / k.users_total) * 100) : 0;

  return (
    <div className="p-6 sm:p-8 max-w-6xl" data-testid="admin-dashboard">
      <div className="flex items-baseline gap-3">
        <h1 className="font-display text-2xl font-semibold">Platform overview</h1>
        <span className="text-xs text-[hsl(var(--muted-foreground))]">live</span>
      </div>

      <div className="mt-5 grid grid-cols-2 md:grid-cols-4 gap-3">
        <KPI label="Users" value={k.users_total} sub={`${k.paid_users} paid · ${paidPct}%`} icon={Users} testid="kpi-users" />
        <KPI label="Active events" value={k.events_active} sub={`${k.events_paid} paid · ${k.events_free} free`} icon={Calendar} testid="kpi-events" />
        <KPI label="Tributes today" value={k.submissions_today} sub={`${k.submissions_week} this week`} icon={Video} testid="kpi-subs" />
        <KPI label="Revenue (lifetime)" value={`$${rev}`} sub={`${k.compilations_ready} films compiled`} icon={DollarSign} testid="kpi-revenue" />
      </div>

      <div className="mt-8 rounded-2xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] p-5">
        <div className="flex items-center gap-2 text-sm font-semibold"><TrendingUp size={14} /> Events by type</div>
        <div className="mt-3 space-y-2" data-testid="kpi-events-by-type">
          {(k.events_by_type || []).sort((a, b) => b.count - a.count).map((row) => (
            <div key={row.type} className="flex items-center gap-3">
              <div className="w-24 text-xs capitalize">{row.type}</div>
              <div className="flex-1 h-2 rounded-full bg-[hsl(var(--muted))] overflow-hidden">
                <div className="h-full bg-[hsl(var(--accent))]" style={{ width: `${Math.min(100, (row.count / Math.max(1, k.events_active)) * 100)}%` }} />
              </div>
              <div className="w-8 text-right tabular-nums text-xs font-semibold">{row.count}</div>
            </div>
          ))}
          {(!k.events_by_type || k.events_by_type.length === 0) && <p className="text-xs text-[hsl(var(--muted-foreground))]">No events yet.</p>}
        </div>
      </div>

      <div className="mt-6 rounded-2xl border border-dashed border-[hsl(var(--border))] p-4 text-xs text-[hsl(var(--muted-foreground))] flex items-start gap-2">
        <Sparkles size={13} className="mt-0.5 shrink-0 text-[hsl(var(--accent))]" />
        Charts over time, funnel, and system health strip come in Phase 8.2 — for now dive into Users, Events, and the Audit log from the sidebar.
      </div>
    </div>
  );
}
